// src/components/GeminiDeepThinkAdvisor/GeminiDeepThinkAdvisor.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import productItems from '../../data/ProductItems';
import './GeminiDeepThinkAdvisor.css';

const GeminiDeepThinkAdvisor = () => {
  const [query, setQuery] = useState('');
  const [thinking, setThinking] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [finalAnswer, setFinalAnswer] = useState(null);
  const [error, setError] = useState('');
  const [showExample, setShowExample] = useState(true);
  const [roomDimensions, setRoomDimensions] = useState({ length: '', width: '', height: '' });
  const [showRoomCalculator, setShowRoomCalculator] = useState(false);
  const [budgetAlert, setBudgetAlert] = useState(null);
  const stepsEndRef = useRef(null);
  const navigate = useNavigate();

  // Sample scenarios
  const exampleScenarios = [
    {
      title: "Home Office Setup",
      query: "I have $1500 budget for a complete home office setup. I work 10+ hours daily doing programming and video editing. I have back pain. I have a small room (10x10 ft) with a window facing east. What should I buy?"
    },
    {
      title: "Living Room Renovation",
      query: "I want to renovate my 250 sq ft living room in modern minimalist style. Budget $3000. Current furniture: old brown sofa, wooden coffee table. Lighting is poor. I want a cozy reading corner and space for guests. What do I need?"
    },
    {
      title: "College Dorm Essentials",
      query: "Moving into college dorm (12x15 ft shared room). Budget $800. Need study space, storage, comfort items. Limited electrical outlets. Can't paint walls. Help me maximize space and functionality."
    }
  ];

  useEffect(() => {
    stepsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thinkingSteps]);

  // Clean markdown formatting
  const cleanMarkdown = (text) => {
    if (!text) return '';

    return text
      .replace(/\|[\s\S]*?\|/g, '')
      .replace(/:\s*-+\s*:?/g, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/#{1,6}\s+/g, '')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/`(.+?)`/g, '$1')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  const callGeminiDeepThink = async (userQuery) => {
    const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

    if (!GEMINI_API_KEY) {
      throw new Error('Gemini API key not found. Please add REACT_APP_GEMINI_API_KEY to .env file');
    }

    const systemPrompt = `You are a shopping advisor. Analyze this scenario and provide recommendations.

USER SCENARIO: ${userQuery}

IMPORTANT: Provide your response in PLAIN TEXT format only. Do NOT use markdown tables, pipes (|), or special formatting. Write in clear paragraphs and bullet points only.

Structure your response as:
1. Analysis Step 1: [topic]
   - Explanation in plain text
   - Key findings as bullet points

2. Analysis Step 2: [topic]
   - Explanation in plain text
   
3. Analysis Step 3: [topic]
   - Explanation in plain text

4. Analysis Step 4: [topic]
   - Explanation in plain text

Then provide:
- Product recommendations with prices
- Budget breakdown
- Alternative options
- Expert advice

Be specific and practical. Use ONLY bullet points and paragraphs, NO tables.`;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-3-flash-preview:generateContent?key=${GEMINI_API_KEY}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{ text: systemPrompt }]
          }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 3000,
          }
        })
      }
    );

    if (!response.ok) {
      await response.json().catch(() => null);

      if (response.status === 404) {
        throw new Error('Model not found. Please check your API configuration.');
      } else if (response.status === 429) {
        throw new Error('API quota exceeded. Please try again later.');
      } else if (response.status === 400) {
        throw new Error('Invalid API request. Please check your API key.');
      } else if (response.status === 403) {
        throw new Error('Invalid API key. Check your .env file.');
      } else {
        throw new Error(`API Error: ${response.status}`);
      }
    }

    const data = await response.json();

    if (!data || !data.candidates || data.candidates.length === 0) {
      throw new Error('No response from AI. Please try again.');
    }

    const textResponse = data.candidates[0]?.content?.parts?.[0]?.text;

    if (!textResponse) {
      throw new Error('Empty response from AI. Please try again.');
    }

    return parseTextToStructuredData(textResponse, userQuery);
  };

  const parseTextToStructuredData = (text, originalQuery) => {
    const cleanedText = cleanMarkdown(text);
    const lines = cleanedText.split('\n').filter(line => line.trim());

    const thinkingSteps = [];
    let stepNumber = 1;

    const stepPatterns = [
      /^(\d+)[.)]\s*(.+?):/i,
      /^Step\s*(\d+)[:\s]+(.+)/i,
      /^(Analysis|Constraint|Budget|Priority|Product|Recommendation)[\s:]+(.+)/i
    ];

    let currentStep = null;

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      let isStepStart = false;
      let stepName = '';

      for (const pattern of stepPatterns) {
        const match = trimmedLine.match(pattern);
        if (match && stepNumber <= 5) {
          isStepStart = true;
          stepName = match[2] || match[1] || trimmedLine;
          break;
        }
      }

      if (isStepStart) {
        if (currentStep) {
          thinkingSteps.push({
            step_number: currentStep.step_number,
            step_name: currentStep.step_name,
            reasoning: currentStep.reasoning.trim(),
            findings: currentStep.findings
          });
        }

        currentStep = {
          step_number: stepNumber++,
          step_name: stepName.substring(0, 80),
          reasoning: '',
          findings: []
        };
      } else if (currentStep) {
        if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
          currentStep.findings.push(trimmedLine.replace(/^[-•]\s*/, ''));
        } else if (!trimmedLine.match(/^[\d.]+$/)) {
          currentStep.reasoning += trimmedLine + ' ';
        }
      }
    });

    if (currentStep) {
      thinkingSteps.push(currentStep);
    }

    while (thinkingSteps.length < 3) {
      thinkingSteps.push({
        step_number: thinkingSteps.length + 1,
        step_name: `Analysis Step ${thinkingSteps.length + 1}`,
        reasoning: cleanedText.substring(0, 250),
        findings: ['Processing scenario details...']
      });
    }

    const budgetMatches = originalQuery.match(/\$?(\d{1,5})/g) || [];
    const budget = budgetMatches.length > 0
      ? parseInt(budgetMatches[0].replace('$', ''))
      : 1000;

    const products = extractRealProducts(cleanedText, budget, originalQuery);

    // ✅ NEW: Calculate space requirements
    const spaceAnalysis = calculateSpaceRequirements(products);

    // ✅ NEW: Check budget alerts
    const budgetCheck = checkBudgetAlert(products, budget);
    setBudgetAlert(budgetCheck);

    return {
      thinking_steps: thinkingSteps.slice(0, 5),
      final_recommendation: {
        total_cost: products.reduce((sum, p) => sum + parseFloat(p.price), 0).toFixed(2),
        products: products,
        budget_breakdown: {
          essential: Math.floor(budget * 0.6),
          important: Math.floor(budget * 0.25),
          optional: Math.floor(budget * 0.10),
          remaining: Math.floor(budget * 0.05)
        },
        alternatives: [
          {
            scenario: "Budget Option",
            total_cost: Math.floor(budget * 0.7),
            trade_offs: "Basic versions of essential items"
          },
          {
            scenario: "Premium Option",
            total_cost: Math.floor(budget * 1.3),
            trade_offs: "Higher quality with extended warranty"
          }
        ],
        advice: extractAdvice(cleanedText),
        // ✅ NEW FEATURES
        spaceAnalysis: spaceAnalysis,
        financingOptions: calculateFinancingOptions(products.reduce((sum, p) => sum + parseFloat(p.price), 0)),
        sustainabilityScore: calculateSustainabilityScore(products),
        deliveryTimeline: estimateDeliveryTimeline(products),
        assemblyComplexity: calculateAssemblyComplexity(products)
      }
    };
  };

  // ✅ NEW: Calculate space requirements
  const calculateSpaceRequirements = (products) => {
    let totalFootprint = 0;
    const itemBreakdown = [];

    products.forEach(product => {
      let sqFt = 0;
      const name = product.name.toLowerCase();
      
      if (name.includes('chair')) sqFt = 6;
      else if (name.includes('desk') || name.includes('table')) sqFt = 15;
      else if (name.includes('sofa')) sqFt = 35;
      else if (name.includes('bed')) sqFt = 45;
      else if (name.includes('shelf') || name.includes('bookshelf')) sqFt = 8;
      else if (name.includes('lamp')) sqFt = 2;
      else sqFt = 5;

      totalFootprint += sqFt;
      itemBreakdown.push({ name: product.name, space: sqFt });
    });

    return {
      totalSquareFeet: totalFootprint,
      recommendedRoomSize: Math.ceil(totalFootprint * 1.5),
      items: itemBreakdown
    };
  };

  // ✅ NEW: Budget alert checker
  const checkBudgetAlert = (products, budget) => {
    const total = products.reduce((sum, p) => sum + parseFloat(p.price), 0);
    const percentUsed = (total / budget) * 100;

    if (percentUsed > 100) {
      return {
        type: 'over',
        message: `⚠️ Over budget by $${(total - budget).toFixed(2)} (${(percentUsed - 100).toFixed(1)}%)`,
        severity: 'danger'
      };
    } else if (percentUsed > 90) {
      return {
        type: 'near',
        message: `⚡ Using ${percentUsed.toFixed(1)}% of budget - ${Math.floor(budget - total)} remaining`,
        severity: 'warning'
      };
    } else {
      return {
        type: 'safe',
        message: `✅ Within budget - $${(budget - total).toFixed(2)} remaining (${(100 - percentUsed).toFixed(1)}% available)`,
        severity: 'success'
      };
    }
  };

  // ✅ NEW: Financing options calculator
  const calculateFinancingOptions = (totalCost) => {
    return [
      {
        name: "6-Month Plan",
        monthlyPayment: (totalCost / 6).toFixed(2),
        interest: 0,
        totalPayment: totalCost.toFixed(2)
      },
      {
        name: "12-Month Plan",
        monthlyPayment: (totalCost * 1.05 / 12).toFixed(2),
        interest: 5,
        totalPayment: (totalCost * 1.05).toFixed(2)
      },
      {
        name: "24-Month Plan",
        monthlyPayment: (totalCost * 1.12 / 24).toFixed(2),
        interest: 12,
        totalPayment: (totalCost * 1.12).toFixed(2)
      }
    ];
  };

  // ✅ NEW: Sustainability score
  const calculateSustainabilityScore = (products) => {
    const categories = {
      'Furniture': 75,
      'Decor': 80,
      'Electronics': 60,
      'Lighting': 85
    };

    const scores = products.map(p => categories[p.category] || 70);
    const avg = scores.reduce((a, b) => a + b, 0) / scores.length;

    return {
      score: avg.toFixed(0),
      rating: avg >= 80 ? 'Excellent' : avg >= 70 ? 'Good' : avg >= 60 ? 'Fair' : 'Poor',
      description: avg >= 80 ? 'Eco-friendly materials and sustainable practices' : 
                   avg >= 70 ? 'Moderate environmental impact' : 
                   'Consider more sustainable alternatives'
    };
  };

  // ✅ NEW: Delivery timeline estimator
  const estimateDeliveryTimeline = (products) => {
    const categories = {
      'Furniture': { min: 5, max: 14 },
      'Electronics': { min: 2, max: 5 },
      'Decor': { min: 3, max: 7 },
      'Lighting': { min: 3, max: 7 }
    };

    const timelines = products.map(p => categories[p.category] || { min: 3, max: 10 });
    const maxDays = Math.max(...timelines.map(t => t.max));

    return {
      estimated: `${Math.min(...timelines.map(t => t.min))}-${maxDays} business days`,
      express: `${Math.floor(maxDays / 2)}-${Math.floor(maxDays * 0.75)} business days (+$${(products.length * 15).toFixed(2)})`,
      standard: `${maxDays}-${maxDays + 7} business days (Free)`
    };
  };

  // ✅ NEW: Assembly complexity calculator
  const calculateAssemblyComplexity = (products) => {
    const complexity = {
      chair: 'Easy',
      desk: 'Moderate',
      table: 'Moderate',
      sofa: 'Moderate',
      bed: 'Complex',
      shelf: 'Moderate',
      lamp: 'Easy'
    };

    const items = products.map(p => {
      const name = p.name.toLowerCase();
      for (const key in complexity) {
        if (name.includes(key)) {
          return { name: p.name, complexity: complexity[key] };
        }
      }
      return { name: p.name, complexity: 'Easy' };
    });

    const complexCount = items.filter(i => i.complexity === 'Complex').length;
    const moderateCount = items.filter(i => i.complexity === 'Moderate').length;

    return {
      items: items,
      summary: complexCount > 0 ? 'Professional assembly recommended' :
               moderateCount > 2 ? 'Moderate DIY skills required' :
               'Easy DIY assembly',
      estimatedTime: `${items.length * 30}-${items.length * 60} minutes`,
      professionalCost: `$${(items.length * 50).toFixed(2)}`
    };
  };

  const extractRealProducts = (text, budget, originalQuery) => {
    const lowerText = text.toLowerCase();
    const lowerQuery = originalQuery.toLowerCase();
    const combinedText = lowerText + ' ' + lowerQuery;

    const categoryMapping = {
      'chair': ['Executive Office Chair', 'Sheen Chair'],
      'desk': ['Office Desk', 'Simple Dining Table'],
      'table': ['Office Desk', 'Simple Dining Table'],
      'sofa': ['Modern Sofa'],
      'bed': ['Queen Size Bed'],
      'lamp': ['Modern Table Lamp'],
      'light': ['Modern Table Lamp'],
      'storage': ['Book Self', 'Wooden Almirah'],
      'shelf': ['Book Self'],
      'bookshelf': ['Book Self'],
      'monitor': ['MacBook Laptop'],
      'laptop': ['MacBook Laptop'],
      'computer': ['MacBook Laptop'],
      'mirror': ['Wall Mirror'],
      'carpet': ['Game Ready Carpet'],
      'rug': ['Game Ready Carpet'],
      'pot': ['Decorative Pot Plant'],
      'plant': ['Decorative Pot Plant'],
      'painting': ['Wooden Frame Painting'],
      'art': ['Wooden Frame Painting'],
      'almirah': ['Wooden Almirah'],
      'wardrobe': ['Wooden Almirah']
    };

    const matchedProducts = [];
    const usedProductIds = new Set();

    Object.keys(categoryMapping).forEach(keyword => {
      if (combinedText.includes(keyword)) {
        const productNames = categoryMapping[keyword];
        productNames.forEach(productName => {
          const product = productItems.find(p => 
            p.name === productName && !usedProductIds.has(p.id)
          );
          if (product) {
            matchedProducts.push(product);
            usedProductIds.add(product.id);
          }
        });
      }
    });

    if (matchedProducts.length === 0) {
      const furnitureProducts = productItems
        .filter(p => p.category === 'Furniture')
        .slice(0, 4);
      matchedProducts.push(...furnitureProducts);
    }

    let selectedProducts = [];
    let currentTotal = 0;

    matchedProducts.forEach(product => {
      const price = parseFloat(product.price);
      if (currentTotal + price <= budget * 0.95 && selectedProducts.length < 6) {
        selectedProducts.push({
          ...product,
          product_id: product.id,
          priority: selectedProducts.length === 0 ? 'Critical' : 
                   selectedProducts.length < 3 ? 'Important' : 'Optional',
          reasoning: `${product.description || 'Quality item for your space'}`,
          // ✅ NEW: Add availability and seller info
          availability: Math.random() > 0.2 ? 'In Stock' : 'Ships in 3-5 days',
          sellers: generateSellerOptions(product)
        });
        currentTotal += price;
      }
    });

    if (selectedProducts.length < 3) {
      const fallbackProducts = productItems.slice(0, 3);
      fallbackProducts.forEach((product, index) => {
        if (!usedProductIds.has(product.id)) {
          selectedProducts.push({
            ...product,
            product_id: product.id,
            priority: index === 0 ? 'Critical' : index === 1 ? 'Important' : 'Optional',
            reasoning: product.description || 'Recommended for your space',
            availability: 'In Stock',
            sellers: generateSellerOptions(product)
          });
        }
      });
    }

    return selectedProducts.slice(0, 6);
  };

  // ✅ NEW: Generate seller options
  const generateSellerOptions = (product) => {
    const basePrice = parseFloat(product.price);
    return [
      {
        name: 'AR-Bazaar Direct',
        price: basePrice.toFixed(2),
        shipping: 'Free',
        rating: 4.8,
        isPrimary: true
      },
      {
        name: 'Alternative Seller A',
        price: (basePrice * 1.05).toFixed(2),
        shipping: '$9.99',
        rating: 4.6
      },
      {
        name: 'Budget Marketplace',
        price: (basePrice * 0.92).toFixed(2),
        shipping: '$14.99',
        rating: 4.2
      }
    ];
  };

  const extractAdvice = (text) => {
    const advice = [];
    const lines = text.split('\n');

    lines.forEach(line => {
      const cleanLine = line.trim();
      if ((cleanLine.includes('consider') || cleanLine.includes('recommend') || cleanLine.includes('should'))
        && cleanLine.length > 20 && cleanLine.length < 150) {
        advice.push(cleanLine.replace(/^[-•*]\s*/, ''));
      }
    });

    if (advice.length === 0) {
      advice.push("Prioritize essential items first within your budget");
      advice.push("Consider quality over quantity for frequently used items");
      advice.push("Look for multi-functional furniture to save space");
    }

    return advice.slice(0, 5);
  };

  const simulateThinkingSteps = (steps, callback) => {
    setThinkingSteps([]);
    let currentStep = 0;

    const revealStep = () => {
      if (currentStep < steps.length) {
        setThinkingSteps(prev => [...prev, steps[currentStep]]);
        currentStep++;
        setTimeout(revealStep, 1500);
      } else {
        callback();
      }
    };

    revealStep();
  };

  const handleAnalyze = async () => {
    if (!query.trim()) {
      setError('Please describe your shopping scenario');
      return;
    }

    setError('');
    setThinking(true);
    setThinkingSteps([]);
    setFinalAnswer(null);
    setShowExample(false);
    setBudgetAlert(null);

    try {
      const result = await callGeminiDeepThink(query);

      simulateThinkingSteps(result.thinking_steps || [], () => {
        setFinalAnswer(result.final_recommendation);
        setThinking(false);
      });

    } catch (err) {
      setError(err.message || 'Failed to analyze. Please try again.');
      setThinking(false);
      console.error('Analysis error:', err);
    }
  };

  const loadExample = (scenario) => {
    setQuery(scenario.query);
    setShowExample(false);
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleAddAllToCart = () => {
    if (finalAnswer && finalAnswer.products) {
      finalAnswer.products.forEach(product => {
        const cartItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          modelSrc: product.modelSrc,
          iOSSrc: product.iOSSrc,
          quantity: 1
        };
        
        const cart = JSON.parse(localStorage.getItem('cart') || '[]');
        const existingItem = cart.find(item => item.id === product.id);
        
        if (!existingItem) {
          cart.push(cartItem);
          localStorage.setItem('cart', JSON.stringify(cart));
        }
      });
      
      alert('All recommended products added to cart!');
    }
  };

  // ✅ NEW: Calculate room compatibility
  const calculateRoomCompatibility = () => {
    if (!roomDimensions.length || !roomDimensions.width) return null;

    const roomArea = parseInt(roomDimensions.length) * parseInt(roomDimensions.width);
    const requiredSpace = finalAnswer?.spaceAnalysis?.totalSquareFeet || 0;
    const percentage = (requiredSpace / roomArea) * 100;

    return {
      roomArea,
      usedSpace: requiredSpace,
      percentage: percentage.toFixed(1),
      status: percentage > 70 ? 'tight' : percentage > 50 ? 'comfortable' : 'spacious'
    };
  };

  // ✅ NEW: Export plan as PDF (mock function)
  const handleExportPlan = () => {
    const planData = {
      query: query,
      products: finalAnswer?.products,
      totalCost: finalAnswer?.total_cost,
      timestamp: new Date().toISOString()
    };

    const dataStr = JSON.stringify(planData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `shopping-plan-${Date.now()}.json`;
    link.click();

    alert('✅ Shopping plan exported! You can print or email this file.');
  };

  return (
    <div className="ai-shop-advisor-container">
      <div className="ai-shop-advisor-header">
        <div className="ai-shop-advisor-header-icon">🧠</div>
        <div className="ai-shop-advisor-header-content">
          <h2>AI Shopping Dilemma Solver</h2>
          <p className="ai-shop-advisor-subtitle">
            Powered by <strong>Gemini Deep Think</strong> - Advanced reasoning for complex shopping decisions
          </p>
        </div>
      </div>

      {showExample && (
        <div className="ai-shop-advisor-example-scenarios">
          <p className="ai-shop-advisor-examples-label">💡 Try an example scenario:</p>
          <div className="ai-shop-advisor-scenario-grid">
            {exampleScenarios.map((scenario, index) => (
              <div
                key={index}
                className="ai-shop-advisor-scenario-card"
                onClick={() => loadExample(scenario)}
              >
                <h4>{scenario.title}</h4>
                <p>{scenario.query.substring(0, 100)}...</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ai-shop-advisor-query-section">
        <label htmlFor="shopping-query">
          <strong>Describe your shopping scenario:</strong>
          <span className="ai-shop-advisor-label-hint">
            Include budget, room size, requirements, constraints, pain points
          </span>
        </label>
        <textarea
          id="shopping-query"
          className="ai-shop-advisor-query-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Example: I need to furnish a small 10x12 ft home office. Budget $1200. I work 8+ hours daily and have back problems. Need good lighting and storage. Window faces north..."
          rows="6"
          disabled={thinking}
        />

        {error && (
          <div className="ai-shop-advisor-error-message">
            ⚠️ {error}
          </div>
        )}

        <button
          className="ai-shop-advisor-analyze-button"
          onClick={handleAnalyze}
          disabled={thinking || !query.trim()}
        >
          {thinking ? (
            <>
              <span className="ai-shop-advisor-spinner"></span>
              AI is thinking deeply...
            </>
          ) : (
            <>
              🧠 Analyze with Deep Think
            </>
          )}
        </button>
      </div>

      {(thinkingSteps.length > 0 || finalAnswer) && (
        <div className="ai-shop-advisor-results-section">
          <h3 className="ai-shop-advisor-results-title">
            <span className="ai-shop-advisor-thinking-icon">🤔</span>
            Deep Reasoning Process
          </h3>

          <div className="ai-shop-advisor-thinking-steps">
            {thinkingSteps.map((step, index) => (
              <div key={index} className="ai-shop-advisor-thinking-step" style={{
                animationDelay: `${index * 0.1}s`
              }}>
                <div className="ai-shop-advisor-step-header">
                  <span className="ai-shop-advisor-step-number">Step {step.step_number}</span>
                  <h4 className="ai-shop-advisor-step-name">{step.step_name}</h4>
                </div>
                <p className="ai-shop-advisor-step-reasoning">{step.reasoning}</p>
                {step.findings && step.findings.length > 0 && (
                  <ul className="ai-shop-advisor-step-findings">
                    {step.findings.map((finding, fIndex) => (
                      <li key={fIndex}>✓ {finding}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <div ref={stepsEndRef} />
          </div>

          {finalAnswer && (
            <div className="ai-shop-advisor-final-recommendation">
              <div className="ai-shop-advisor-recommendation-header">
                <span className="ai-shop-advisor-check-icon">✅</span>
                <h3>Optimal Shopping Plan</h3>
              </div>

              {/* ✅ NEW: Budget Alert */}
              {budgetAlert && (
                <div className={`ai-shop-advisor-budget-alert ${budgetAlert.severity}`}>
                  {budgetAlert.message}
                </div>
              )}

              <div className="ai-shop-advisor-budget-summary">
                <div className="ai-shop-advisor-budget-card ai-shop-advisor-budget-card-total">
                  <span className="ai-shop-advisor-budget-label">Total Cost</span>
                  <span className="ai-shop-advisor-budget-amount">${finalAnswer.total_cost}</span>
                </div>
                {finalAnswer.budget_breakdown && (
                  <>
                    <div className="ai-shop-advisor-budget-card">
                      <span className="ai-shop-advisor-budget-label">Essential</span>
                      <span className="ai-shop-advisor-budget-amount">${finalAnswer.budget_breakdown.essential}</span>
                    </div>
                    <div className="ai-shop-advisor-budget-card">
                      <span className="ai-shop-advisor-budget-label">Important</span>
                      <span className="ai-shop-advisor-budget-amount">${finalAnswer.budget_breakdown.important}</span>
                    </div>
                    <div className="ai-shop-advisor-budget-card ai-shop-advisor-budget-card-remaining">
                      <span className="ai-shop-advisor-budget-label">Remaining</span>
                      <span className="ai-shop-advisor-budget-amount">${finalAnswer.budget_breakdown.remaining}</span>
                    </div>
                  </>
                )}
              </div>

              {/* ✅ NEW: Space Analysis */}
              {finalAnswer.spaceAnalysis && (
                <div className="ai-shop-advisor-space-analysis">
                  <h4>📐 Space Requirements</h4>
                  <div className="space-stats">
                    <div className="space-stat">
                      <span className="stat-label">Total Footprint:</span>
                      <span className="stat-value">{finalAnswer.spaceAnalysis.totalSquareFeet} sq ft</span>
                    </div>
                    <div className="space-stat">
                      <span className="stat-label">Recommended Room Size:</span>
                      <span className="stat-value">{finalAnswer.spaceAnalysis.recommendedRoomSize}+ sq ft</span>
                    </div>
                  </div>
                  <button 
                    className="ai-shop-advisor-btn-secondary"
                    onClick={() => setShowRoomCalculator(!showRoomCalculator)}
                  >
                    {showRoomCalculator ? '🔼 Hide' : '📏 Check My Room Fit'}
                  </button>
                  
                  {showRoomCalculator && (
                    <div className="room-calculator">
                      <div className="room-inputs">
                        <input
                          type="number"
                          placeholder="Length (ft)"
                          value={roomDimensions.length}
                          onChange={(e) => setRoomDimensions({...roomDimensions, length: e.target.value})}
                        />
                        <input
                          type="number"
                          placeholder="Width (ft)"
                          value={roomDimensions.width}
                          onChange={(e) => setRoomDimensions({...roomDimensions, width: e.target.value})}
                        />
                      </div>
                      {roomDimensions.length && roomDimensions.width && (
                        <div className="room-result">
                          {(() => {
                            const compat = calculateRoomCompatibility();
                            return (
                              <p className={`status-${compat.status}`}>
                                {compat.status === 'spacious' && '✅ Perfect fit! Room is spacious enough'}
                                {compat.status === 'comfortable' && '👍 Good fit with comfortable space'}
                                {compat.status === 'tight' && '⚠️ Tight fit - consider removing optional items'}
                              </p>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {finalAnswer.products && finalAnswer.products.length > 0 && (
                <div className="ai-shop-advisor-product-recommendations">
                  <h4>Recommended Products</h4>
                  {finalAnswer.products.map((product, index) => {
                    const priorityClass = product.priority ? product.priority.toLowerCase() : 'info';
                    return (
                      <div 
                        key={index} 
                        className="ai-shop-advisor-product-recommendation"
                        onClick={() => handleProductClick(product.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="ai-shop-advisor-product-item-header">
                          <span className={`ai-shop-advisor-priority-badge ai-shop-advisor-priority-${priorityClass}`}>
                            {product.priority || 'Info'}
                          </span>
                          <h5 className="ai-shop-advisor-product-item-name">{product.name}</h5>
                          <span className="ai-shop-advisor-product-item-price">${product.price}</span>
                        </div>
                        <p className="ai-shop-advisor-product-item-reasoning">{product.reasoning}</p>
                        
                        {/* ✅ NEW: Availability & Sellers */}
                        <div className="product-meta">
                          <span className="availability">{product.availability}</span>
                          {product.sellers && product.sellers.length > 1 && (
                            <span className="sellers-count">
                              {product.sellers.length} sellers available
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ✅ NEW: Financing Options */}
              {finalAnswer.financingOptions && (
                <div className="ai-shop-advisor-financing-section">
                  <h4>💳 Financing Options</h4>
                  <div className="financing-grid">
                    {finalAnswer.financingOptions.map((option, index) => (
                      <div key={index} className="financing-card">
                        <h5>{option.name}</h5>
                        <div className="monthly-payment">${option.monthlyPayment}/mo</div>
                        <p className="financing-details">
                          {option.interest === 0 ? '0% Interest' : `${option.interest}% APR`}
                          <br />
                          Total: ${option.totalPayment}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ✅ NEW: Sustainability Score */}
              {finalAnswer.sustainabilityScore && (
                <div className="ai-shop-advisor-sustainability-section">
                  <h4>🌱 Sustainability Score</h4>
                  <div className="sustainability-card">
                    <div className="score-circle">
                      <span className="score-number">{finalAnswer.sustainabilityScore.score}</span>
                      <span className="score-label">/100</span>
                    </div>
                    <div className="sustainability-info">
                      <div className="rating">{finalAnswer.sustainabilityScore.rating}</div>
                      <p>{finalAnswer.sustainabilityScore.description}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ NEW: Delivery Timeline */}
              {finalAnswer.deliveryTimeline && (
                <div className="ai-shop-advisor-delivery-section">
                  <h4>🚚 Estimated Delivery</h4>
                  <div className="delivery-options">
                    <div className="delivery-option">
                      <strong>Standard:</strong> {finalAnswer.deliveryTimeline.standard}
                    </div>
                    <div className="delivery-option">
                      <strong>Express:</strong> {finalAnswer.deliveryTimeline.express}
                    </div>
                  </div>
                </div>
              )}

              {/* ✅ NEW: Assembly Complexity */}
              {finalAnswer.assemblyComplexity && (
                <div className="ai-shop-advisor-assembly-section">
                  <h4>🔧 Assembly Information</h4>
                  <div className="assembly-summary">
                    <p><strong>Complexity:</strong> {finalAnswer.assemblyComplexity.summary}</p>
                    <p><strong>Estimated Time:</strong> {finalAnswer.assemblyComplexity.estimatedTime}</p>
                    <p><strong>Professional Assembly:</strong> {finalAnswer.assemblyComplexity.professionalCost}</p>
                  </div>
                </div>
              )}

              {finalAnswer.alternatives && finalAnswer.alternatives.length > 0 && (
                <div className="ai-shop-advisor-alternatives-section">
                  <h4>💡 Alternative Options</h4>
                  <div className="ai-shop-advisor-alternatives-grid">
                    {finalAnswer.alternatives.map((alt, index) => (
                      <div key={index} className="ai-shop-advisor-alternative-card">
                        <h5>{alt.scenario}</h5>
                        <p className="ai-shop-advisor-alt-cost"><strong>Cost:</strong> ${alt.total_cost}</p>
                        <p className="ai-shop-advisor-alt-tradeoffs"><strong>Trade-offs:</strong> {alt.trade_offs}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {finalAnswer.advice && finalAnswer.advice.length > 0 && (
                <div className="ai-shop-advisor-advice-section">
                  <h4>📝 Expert Advice</h4>
                  <ul className="ai-shop-advisor-advice-list">
                    {finalAnswer.advice.map((tip, index) => (
                      <li key={index}>
                        <span className="ai-shop-advisor-tip-icon">💡</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="ai-shop-advisor-action-buttons">
                <button 
                  className="ai-shop-advisor-btn-primary"
                  onClick={handleAddAllToCart}
                >
                  🛒 Add All to Cart
                </button>
                <button 
                  className="ai-shop-advisor-btn-secondary"
                  onClick={handleExportPlan}
                >
                  📤 Export Plan
                </button>
                <button className="ai-shop-advisor-btn-secondary" onClick={() => {
                  setQuery('');
                  setThinkingSteps([]);
                  setFinalAnswer(null);
                  setShowExample(true);
                  setError('');
                  setBudgetAlert(null);
                  setRoomDimensions({ length: '', width: '', height: '' });
                  setShowRoomCalculator(false);
                }}>
                  🔄 New Scenario
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="ai-shop-advisor-footer">
        <p className="ai-shop-advisor-footer-note">
          <strong>🧠 Powered by Gemini Deep Think:</strong> This feature uses advanced multi-step reasoning
          to analyze complex shopping scenarios with multiple constraints, trade-offs, and priorities.
        </p>
      </div>
    </div>
  );
};

export default GeminiDeepThinkAdvisor;